import { doc, serverTimestamp, updateDoc, writeBatch } from 'firebase/firestore';

import type { MatchMutation, PlannedMatch } from '@/core/engine/contracts';
import { advanceIfComplete, planPhase } from '@/core/engine/orchestrator';
import { splitProgression, type BlockedMutation } from '@/core/engine/progression';
import { toSeeds } from '@/core/engine/seeding';
import { DomainError } from '@/core/errors/DomainError';
import {
  areSettingsEditable,
  type Match,
  type Team,
  type Tournament,
  type Uid,
} from '@/core/schemas';
import { toAppError } from '@/infrastructure/errors/AppError';
import { firestore } from '@/infrastructure/firebase/app';
import { firestorePaths } from '@/infrastructure/firestore/paths';

/** Firestore commits at most 500 operations per batch. */
const BATCH_LIMIT = 400;

// Re-exported so existing imports (`tournamentService.toSeeds`) keep working.
export { toSeeds };

/** What a progression run changed, and what it deliberately did not. */
export interface ProgressionOutcome {
  readonly applied: number;
  readonly blocked: readonly BlockedMutation[];
}

function toMatchDocument(planned: PlannedMatch, tournamentId: Tournament['id'], actorUid: Uid) {
  return {
    id: planned.id,
    tournamentId,
    phaseId: planned.phaseId,
    roundKey: planned.roundKey,
    groupId: planned.groupId,
    bracketSlot: planned.bracketSlot,
    order: planned.order,
    home: planned.home,
    away: planned.away,
    boardNumber: null,
    status: 'pending',
    counter: null,
    confirmations: {},
    result: null,
    calledAt: null,
    startedAt: null,
    finishedAt: null,
    createdAt: serverTimestamp(),
    createdBy: actorUid,
    updatedAt: serverTimestamp(),
    updatedBy: actorUid,
  };
}

async function commitInChunks(
  operations: readonly ((batch: ReturnType<typeof writeBatch>) => void)[],
) {
  for (let start = 0; start < operations.length; start += BATCH_LIMIT) {
    const batch = writeBatch(firestore);
    for (const apply of operations.slice(start, start + BATCH_LIMIT)) {
      apply(batch);
    }
    await batch.commit();
  }
}

/**
 * Starts the tournament: plans the first phase with the engine and writes the
 * resulting matches.
 *
 * The engine stays pure — it returns matches, this function persists them. The
 * same split is what would let a Cloud Function take over the writing later
 * without touching the tournament logic.
 */
export async function startTournament(input: {
  tournament: Tournament;
  teams: readonly Team[];
  actorUid: Uid;
}): Promise<{ phaseId: string; matchCount: number }> {
  const { tournament, teams, actorUid } = input;

  const firstPhase = tournament.settings.phases[0];
  if (firstPhase === undefined) {
    throw new DomainError('INVALID_PHASE_CONFIG', 'Das Turnier hat keine Phase konfiguriert.');
  }

  const participants = toSeeds(teams);
  if (participants.length < 2) {
    throw new DomainError(
      'INVALID_STATE_TRANSITION',
      'Für einen Turnierstart werden mindestens zwei angemeldete Teams benötigt.',
      { participants: participants.length },
    );
  }

  const plan = planPhase(tournament, firstPhase.id, participants);

  try {
    await commitInChunks(
      plan.matches.map((planned) => (batch: ReturnType<typeof writeBatch>) => {
        batch.set(
          doc(firestore, firestorePaths.match(tournament.id, planned.id)),
          toMatchDocument(planned, tournament.id, actorUid),
        );
      }),
    );

    const closing = writeBatch(firestore);
    closing.update(doc(firestore, firestorePaths.tournament(tournament.id)), {
      status: 'running',
      currentPhaseId: firstPhase.id,
      startedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      updatedBy: actorUid,
    });
    await closing.commit();

    return { phaseId: firstPhase.id, matchCount: plan.matches.length };
  } catch (error) {
    throw toAppError(error);
  }
}

/**
 * Writes bracket progression back to the matches.
 *
 * Only slots that actually change are written, so calling this after every
 * result is cheap and idempotent. A slot whose participant would change in a
 * match that has already been played on is *not* written — it comes back as
 * `blocked` for the director to resolve (see {@link splitProgression}).
 */
export async function applyProgression(input: {
  tournament: Tournament;
  matches: readonly Match[];
  mutations: readonly MatchMutation[];
  actorUid: Uid;
}): Promise<ProgressionOutcome> {
  const { tournament, matches, mutations, actorUid } = input;
  const { applicable, blocked } = splitProgression(mutations, matches);

  if (applicable.length === 0) {
    return { applied: 0, blocked };
  }

  try {
    await commitInChunks(
      applicable.map((mutation) => (batch: ReturnType<typeof writeBatch>) => {
        batch.update(doc(firestore, firestorePaths.match(tournament.id, mutation.matchId)), {
          [mutation.side]: mutation.participant,
          updatedAt: serverTimestamp(),
          updatedBy: actorUid,
        });
      }),
    );

    return { applied: applicable.length, blocked };
  } catch (error) {
    throw toAppError(error);
  }
}

/**
 * Plans the following phase once the current one is finished.
 *
 * Returns `null` while the phase is still running, so the caller may invoke it
 * after every result without checking first.
 */
export async function advancePhase(input: {
  tournament: Tournament;
  teams: readonly Team[];
  matches: readonly Match[];
  actorUid: Uid;
}): Promise<{ phaseId: string; matchCount: number } | null> {
  const { tournament, teams, matches, actorUid } = input;
  const currentPhaseId = tournament.currentPhaseId;

  if (currentPhaseId === null) {
    return null;
  }

  const transition = advanceIfComplete(tournament, currentPhaseId, toSeeds(teams), matches);

  if (transition === null) {
    return null;
  }

  const closing = writeBatch(firestore);

  if (transition.nextPhaseId === null) {
    closing.update(doc(firestore, firestorePaths.tournament(tournament.id)), {
      status: 'completed',
      currentPhaseId: null,
      completedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      updatedBy: actorUid,
    });
    await closing.commit();
    return null;
  }

  const plan = planPhase(tournament, transition.nextPhaseId, transition.qualifiers);

  try {
    await commitInChunks(
      plan.matches.map((planned) => (batch: ReturnType<typeof writeBatch>) => {
        batch.set(
          doc(firestore, firestorePaths.match(tournament.id, planned.id)),
          toMatchDocument(planned, tournament.id, actorUid),
        );
      }),
    );

    closing.update(doc(firestore, firestorePaths.tournament(tournament.id)), {
      currentPhaseId: transition.nextPhaseId,
      updatedAt: serverTimestamp(),
      updatedBy: actorUid,
    });
    await closing.commit();

    return { phaseId: transition.nextPhaseId, matchCount: plan.matches.length };
  } catch (error) {
    throw toAppError(error);
  }
}

/**
 * Opens or closes the team registration.
 *
 * Both halves of the gate move together on purpose: `registerTeam` accepts a
 * team only when the status is `registration` **and** the setting says open
 * (functions/src/team.ts). Keeping them in one action is what stops the two
 * from drifting apart — a tournament that looks open but rejects every team.
 */
export async function setRegistrationOpen(input: {
  tournament: Tournament;
  isOpen: boolean;
  actorUid: Uid;
}): Promise<void> {
  const { tournament, isOpen, actorUid } = input;

  if (!areSettingsEditable(tournament.status)) {
    throw new DomainError(
      'INVALID_STATE_TRANSITION',
      'Die Anmeldung lässt sich nur vor dem Turnierstart öffnen oder schließen.',
      { status: tournament.status },
    );
  }

  try {
    await updateDoc(doc(firestore, firestorePaths.tournament(tournament.id)), {
      status: 'registration',
      'settings.registration.isOpen': isOpen,
      updatedAt: serverTimestamp(),
      updatedBy: actorUid,
    });
  } catch (error) {
    throw toAppError(error);
  }
}

/** Pauses or resumes a running tournament. */
export async function setTournamentPaused(input: {
  tournament: Tournament;
  paused: boolean;
  actorUid: Uid;
}): Promise<void> {
  const { tournament, paused, actorUid } = input;
  const target = paused ? 'paused' : 'running';

  if (paused ? tournament.status !== 'running' : tournament.status !== 'paused') {
    throw new DomainError(
      'INVALID_STATE_TRANSITION',
      paused
        ? 'Nur ein laufendes Turnier kann pausiert werden.'
        : 'Nur ein pausiertes Turnier kann fortgesetzt werden.',
      { status: tournament.status },
    );
  }

  try {
    await updateDoc(doc(firestore, firestorePaths.tournament(tournament.id)), {
      status: target,
      updatedAt: serverTimestamp(),
      updatedBy: actorUid,
    });
  } catch (error) {
    throw toAppError(error);
  }
}

/**
 * Resets the tournament back to setup: deletes every match and clears the
 * running state, so a fresh draw can be made. Teams and settings are kept.
 *
 * Deliberately destructive and admin-only — the confirmation lives in the UI.
 */
export async function resetTournament(input: {
  tournament: Tournament;
  matches: readonly Match[];
  actorUid: Uid;
}): Promise<number> {
  const { tournament, matches, actorUid } = input;

  try {
    await commitInChunks(
      matches.map((match) => (batch: ReturnType<typeof writeBatch>) => {
        batch.delete(doc(firestore, firestorePaths.match(tournament.id, match.id)));
      }),
    );

    const closing = writeBatch(firestore);
    closing.update(doc(firestore, firestorePaths.tournament(tournament.id)), {
      status: 'setup',
      currentPhaseId: null,
      startedAt: null,
      completedAt: null,
      updatedAt: serverTimestamp(),
      updatedBy: actorUid,
    });
    await closing.commit();

    return matches.length;
  } catch (error) {
    throw toAppError(error);
  }
}
